<script setup lang="ts">
import MarkdownIt from 'markdown-it'
import { VButton, VIcon } from '@/modules/shared/components'
import { useTaskListStore } from '../stores/tasking-store'
import { type Task } from '../types'
import TaskStatusCheckBox from './TaskStatusCheckBox.vue'

defineProps<{
  isExpanded: boolean
  isLoading: boolean
  task: Task
}>()

const store = useTaskListStore()

const toggleTaskStatus = (task: Task) => {
  console.log('toggleTaskStatus', task, store.isLoading(task.id))
  if (store.isLoading(task.id)) return
  if (store.isTaskIncomplete(task)) {
    // opimistic update
    task.status = 'complete'
    task.children?.forEach((subtask) => {
      subtask.status = 'complete'
    })

    store.completeTask(task.id)
  } else {
    // opimistic update–failure
    // NOTE if business logic changes
    task.status = 'incomplete'
    task.children?.forEach((subtask) => {
      subtask.status = 'incomplete'
    })
    store.reopenTask(task.id)
  }
}

const markdown = new MarkdownIt()
</script>

<template>
  <div
    class="task cursor-pointer rounded border transition-[max-height]"
    :class="
      store.isTaskComplete(task)
        ? 'border-gray-300 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-400 opacity-60'
        : 'border-gray-200 bg-white text-gray-700'
    "
  >
    <div class="flex items-start space-x-3 px-4 py-3">
      <!-- chevron -->
      <div class="mt-1" :class="isExpanded ? '' : '-rotate-90'">
        <VIcon name="chevron_down" />
      </div>
      <!-- checkbox -->
      <TaskStatusCheckBox
        @click.stop="() => toggleTaskStatus(task)"
        :checked="store.isTaskComplete(task)"
        :disabled="store.isLoading(task.id)"
        class="mt-1"
      />
      <!-- task name and description -->
      <div class="flex-1">
        <div class="flex items-center justify-between">
          <span
            class="font-medium"
            :class="
              store.isTaskComplete(task)
                ? 'text-gray-500 line-through decoration-gray-500/50 decoration-2'
                : 'text-gray-800'
            "
            >{{ task.name }}</span
          >
          <a v-if="task.action_text" @click.stop :href="task.action_url">
            <VButton :disabled="store.isTaskComplete(task)" size="xs">{{ task.action_text }}</VButton>
          </a>
        </div>
        <transition name="fade">
          <div
            v-if="isExpanded"
            @click.stop
            class="task-description mt-1 text-sm text-gray-700"
            v-html="markdown.render(task.description)"
          ></div>
        </transition>
      </div>
    </div>
    <ul v-show="isExpanded" class="ml-7 px-4 pb-3">
      <li
        v-for="subtask in task?.children"
        :key="subtask.id"
        @click.stop
        class="subtask flex items-center space-x-3"
        :class="[store.isTaskComplete(subtask) ? 'task-status-complete' : '']"
      >
        <!-- checkbox -->
        <TaskStatusCheckBox
          @click.stop="() => toggleTaskStatus(subtask)"
          :checked="store.isTaskComplete(subtask)"
          :disabled="store.isLoading(subtask.id)"
        />
        <!-- subtask name and description -->
        <div class="task-name-and-description" :class="store.isTaskComplete(subtask) ? 't' : 'text-gray-800'">
          <span class="task-name">{{ subtask.name }}</span> <span v-if="subtask.description !== null"> - </span>
          <span class="task-description">{{ subtask.description }}</span>
        </div>
      </li>
    </ul>
  </div>
</template>

<style>
.task-description p a {
  @apply inline-block cursor-pointer whitespace-break-spaces font-medium text-[#51889B] underline decoration-[#51889B]/50 hover:text-[#3d778a] hover:decoration-[#3d778a]/60 active:scale-[0.99];
}

.subtask {
  @apply cursor-default border-b py-1.5 last:border-b-0;
}

.subtask .task-name-and-description {
  @apply text-sm;
}

.subtask .task-name {
  @apply font-medium text-gray-800;
}

.subtask .task-description {
  @apply text-gray-600;
}
.subtask.task-status-complete {
  @apply italic text-gray-500 line-through decoration-gray-600/60 decoration-[1.5px];
}

.subtask.task-status-complete .task-name {
  @apply text-gray-500;
}
.subtask.task-status-complete .task-description {
  @apply text-gray-500;
}
</style>
